import React from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Loader2, PlusCircle, Edit3, Target, AlertTriangle } from "lucide-react";

export function ContentOpportunitiesBoard({ scan, resultsLoading }) {
  const isProcessing = scan?.section_statuses?.content_opportunities?.status === "processing";
  const isFailed = scan?.section_statuses?.content_opportunities?.status === "failed";
  const opportunities = scan?.content_opportunities || [];

  if (resultsLoading || (isProcessing && !["completed", "partial", "failed"].includes(scan?.status) && !opportunities.length)) {
    return (
      <Card className="p-12 flex flex-col items-center justify-center text-center">
        <Loader2 className="h-8 w-8 text-[#FEAB3D] animate-spin mb-4" />
        <p className="font-medium text-slate-700">Generating Content Opportunities…</p>
        <p className="text-sm text-slate-500 mt-2">
          Discovering content gaps and new topic clusters to target.
        </p>
      </Card>
    );
  }

  if (isFailed && !opportunities.length) {
    return (
      <Card className="p-8 text-center bg-red-50 border-red-200">
        <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-3" />
        <p className="font-medium text-red-700">Failed to generate Content Opportunities</p>
        <p className="text-sm text-red-600 mt-1">Please retry this section.</p>
      </Card>
    );
  }

  if (!opportunities.length) {
    return (
      <Card className="p-8 text-center text-slate-500">
        No content opportunities generated yet.
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
      {opportunities.map((opp, idx) => {
        const isNew = opp.type === "create_new";
        return (
          <Card key={idx} className={`flex flex-col h-full overflow-hidden transition-all hover:shadow-md border-t-4 ${isNew ? 'border-t-emerald-500' : 'border-t-[#FEAB3D]'}`}>
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-start justify-between mb-3 gap-2">
                <Badge variant="outline" className={`font-semibold uppercase tracking-wider ${isNew ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-[#FEAB3D]/10 text-[#FEAB3D] border-[#FEAB3D]/30'}`}>
                  {isNew ? <><PlusCircle className="h-3 w-3 mr-1 inline" /> Create New</> : <><Edit3 className="h-3 w-3 mr-1 inline" /> Improve Existing</>}
                </Badge>
                <Badge variant="secondary" className="bg-slate-100 text-slate-600 capitalize">
                  {opp.priority} Priority
                </Badge>
              </div>

              <h3 className="font-bold text-lg text-slate-800 mb-2 leading-tight">
                {opp.suggested_title || opp.affected_topic}
              </h3>

              {!isNew && opp.existing_page && (
                <div className="text-sm text-[#FEAB3D] mb-3 truncate" title={opp.existing_page}>
                  <a href={opp.existing_page} target="_blank" rel="noreferrer" className="hover:underline">
                    {opp.existing_page}
                  </a>
                </div>
              )}

              <p className="text-sm text-slate-600 mb-4 flex-1">
                {opp.reason}
              </p>

              {opp.evidence && <details className="text-sm text-slate-600 mb-4"><summary className="cursor-pointer font-medium">Evidence</summary><p className="mt-2">{opp.evidence}</p></details>}
              {opp.recommended_action && <p className="text-sm text-slate-700 mb-4"><strong>Recommended action:</strong> {opp.recommended_action}</p>}
              {isNew && opp.recommended_url && <p className="text-sm break-all mb-3">Suggested URL: {opp.recommended_url}</p>}
              {[['Weak or missing sections', opp.weak_sections], ['Recommended additions', opp.recommended_additions], ['Questions to answer', opp.questions_to_answer]].map(([label, values]) => Array.isArray(values) && values.length > 0 ? <div key={label} className="text-sm mb-4"><strong>{label}</strong><ul className="list-disc pl-4">{values.map((value, i) => <li key={i}>{value}</li>)}</ul></div> : null)}
              {opp.outline?.length > 0 && <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 border mt-auto">
                <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-slate-500 uppercase">
                  <Target className="h-3 w-3" />
                  Suggested Outline
                </div>
                <ul className="text-sm text-slate-700 space-y-1.5 pl-4 list-disc marker:text-slate-400">
                  {opp.outline?.slice(0, 4).map((bullet, bIdx) => (
                    <li key={bIdx}>{bullet}</li>
                  ))}
                  {opp.outline?.length > 4 && (
                    <li className="text-slate-400 italic text-xs">+{opp.outline.length - 4} more points</li>
                  )}
                </ul>
              </div>}

              <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                {opp.intent && <span>Intent: <span className="font-medium text-slate-700">{opp.intent}</span></span>}
                {opp.confidence && <span>Confidence: <span className="font-medium text-slate-700">{opp.confidence}</span></span>}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
